import { ChevronDown, ChevronUp, X, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MoneyInput } from '../MoneyInput';
import CategorySelector from '../CategorySelector';
import { MonthCalendarPicker } from '../MonthCalendarPicker';
import { useState, useMemo } from 'react';
import type { TransactionFormProps, CategorySplitWithData } from './types';
import type { CategoryWithMonthData } from '../../types/category';

export default function TransactionCard({
  transaction,
  index,
  mode,
  isOnlyCard,
  accounts,
  allMonthPlans,
  budgetMonthCategories,
  loadingBudgetCategories,
  formatMoney,
  formatMonth,
  onUpdate,
  onRemove,
  onToggleExpand,
  onBudgetMonthChange,
}: TransactionFormProps) {
  const { t } = useTranslation();
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const { type, amount, categorySplits, selectedAccount, fromAccount, toAccount, description, date, selectedBudgetMonthPlan, isExpanded, hasError, errorMessage } = transaction;

  // Flatten categories to get all subcategories with parent names
  const allSubcategories = useMemo(() => 
    budgetMonthCategories.flatMap(group =>
      (group.children || []).map(cat => ({
        ...cat,
        parentName: group.name
      }))
    ), [budgetMonthCategories]);

  // Filter categories based on selected account (exclude linked system groups for CC/Paylater/Loan)
  const getFilteredCategories = () => {
    const accountId = type === 'transfer' ? fromAccount : selectedAccount;
    if (!accountId) return allSubcategories;
    
    const account = accounts.find(a => a.id === accountId);
    if (!account) return allSubcategories;
    
    if (!['credit_card', 'paylater', 'loan'].includes(account.type)) {
      return allSubcategories;
    }

    const linkedSystemGroup = budgetMonthCategories.find(group =>
      group.is_system_group && group.linked_account_id === accountId
    );
    
    if (!linkedSystemGroup) return allSubcategories;
    return allSubcategories.filter(cat => cat.parent_id !== linkedSystemGroup.id);
  };

  // Note: filteredCategories could be used in the future for category filtering
  // const filteredCategories = getFilteredCategories();

  // Helper function to get pay full amount for a category
  const getCategoryPayFullData = (cat: CategoryWithMonthData) => {
    if (cat.is_goal) {
      if (cat.goal_monthly_target) {
        const remaining = Math.max(0, cat.goal_monthly_target - (cat.transaction || 0));
        return { amount: remaining, label: t('transaction.remaining') };
      } else {
        const remaining = Math.max(0, (cat.goal_target || 0) - (cat.goal_saved || 0));
        return { amount: remaining, label: t('transaction.remaining') };
      }
    }
    // Non-goal: use balance (ensure non-negative for overspent categories)
    return { amount: Math.max(0, cat.balance || 0), label: t('transaction.remaining') };
  };

  // Calculate total from splits
  const totalFromSplits = categorySplits.reduce((sum, split) => sum + split.amount, 0);

  // Handle type change
  const handleTypeChange = (newType: 'expense' | 'income' | 'transfer') => {
    if (newType === type) return;
    
    const updates: Partial<typeof transaction> = { type: newType, hasError: false, errorMessage: undefined };
    
    if (newType !== 'expense') {
      updates.categorySplits = [];
    }
    if (newType === 'transfer') {
      updates.selectedAccount = '';
    } else {
      updates.fromAccount = '';
      updates.toAccount = '';
    }
    
    onUpdate(index, updates);
  };

  // Handle category toggle - Mobile allows multiple categories
  const handleToggleCategory = (categoryId: string) => {
    if (categorySplits.some(s => s.category_id === categoryId)) {
      // Deselect - remove this specific category
      const newSplits = categorySplits.filter(s => s.category_id !== categoryId);
      onUpdate(index, { categorySplits: newSplits });
    } else {
      // Select - add to existing categories
      const categoryData = allSubcategories.find(c => c.id === categoryId);
      if (categoryData) {
        const payFullData = getCategoryPayFullData(categoryData);
        const newSplit: CategorySplitWithData = {
          category_id: categoryId,
          amount: 0,
          categoryName: categoryData.name,
          parentName: categoryData.parentName,
          remainingAllocation: payFullData.amount,
          isGoal: categoryData.is_goal,
          goalTargetMonth: categoryData.goal_target_month,
          displayLabel: payFullData.label
        };
        // Append to existing splits (multi-category support)
        onUpdate(index, { categorySplits: [...categorySplits, newSplit], hasError: false, errorMessage: undefined });
      }
    }
  };

  // Handle batch category toggle - Mobile allows multiple categories (e.g., group level selection)
  const handleBatchToggleCategories = (categoryIds: string[], shouldSelect: boolean) => {
    if (shouldSelect && categoryIds.length > 0) {
      // Add all selected categories
      const newSplits: CategorySplitWithData[] = categoryIds
        .filter(categoryId => !categorySplits.some(s => s.category_id === categoryId)) // Filter out already selected
        .map(categoryId => {
          const categoryData = allSubcategories.find(c => c.id === categoryId);
          const payFullData = categoryData ? getCategoryPayFullData(categoryData) : { amount: 0, label: '' };
          return {
            category_id: categoryId,
            amount: 0,
            categoryName: categoryData?.name || '',
            parentName: categoryData?.parentName || '',
            remainingAllocation: payFullData.amount,
            isGoal: categoryData?.is_goal,
            goalTargetMonth: categoryData?.goal_target_month,
            displayLabel: payFullData.label
          };
        });
      onUpdate(index, { categorySplits: [...categorySplits, ...newSplits], hasError: false, errorMessage: undefined });
    } else {
      // Deselect all categories in the batch
      const newSplits = categorySplits.filter(s => !categoryIds.includes(s.category_id));
      onUpdate(index, { categorySplits: newSplits });
    }
  };

  // Handle split amount update
  const handleUpdateSplitAmount = (categoryId: string, newAmount: number) => {
    onUpdate(index, {
      categorySplits: categorySplits.map(s =>
        s.category_id === categoryId ? { ...s, amount: newAmount } : s
      )
    });
  };

  // Handle remove split
  const handleRemoveSplit = (categoryId: string) => {
    onUpdate(index, { categorySplits: categorySplits.filter(s => s.category_id !== categoryId) });
  };

  // Handle pay full split
  const handlePayFullSplit = (categoryId: string) => {
    const split = categorySplits.find(s => s.category_id === categoryId);
    if (split && split.remainingAllocation) {
      handleUpdateSplitAmount(categoryId, split.remainingAllocation);
    }
  };

  // Handle pay all full
  const handlePayAllFull = () => {
    const updated = categorySplits.map(s => ({ ...s, amount: s.remainingAllocation || 0 }));
    const total = updated.reduce((sum, s) => sum + s.amount, 0);
    onUpdate(index, { categorySplits: updated, amount: total });
  };

  // Get summary text for collapsed card
  const getSummaryText = () => {
    const parts: string[] = [];
    if (type === 'expense') parts.push(t('transaction.expense'));
    else if (type === 'income') parts.push(t('transaction.income'));
    else parts.push(t('transaction.transfer'));
    
    if (totalFromSplits > 0 || amount > 0) {
      parts.push(formatMoney(categorySplits.length > 0 ? totalFromSplits : amount));
    }
    if (description) parts.push(`"${description}"`);
    
    return parts.join(' • ');
  };

  // Card header color based on type and state
  const getHeaderStyle = () => {
    if (hasError) return 'bg-red-100 border-red-400';
    if (type === 'expense') return 'bg-red-50 border-red-300';
    if (type === 'income') return 'bg-green-50 border-[#077f5d]';
    return 'bg-blue-50 border-blue-300';
  };

  return (
    <div className={`bg-white border-2 ${hasError ? 'border-red-500' : 'border-[#077f5d]'} overflow-hidden`} style={{ boxShadow: hasError ? '3px 3px 0 #dc2626' : '3px 3px 0 #077f5d' }}>
      {/* Month Calendar Picker Modal */}
      {showMonthPicker && (
        <MonthCalendarPicker
          availableMonths={allMonthPlans.map(mp => mp.month)}
          currentMonth={allMonthPlans.find(mp => mp.id === selectedBudgetMonthPlan)?.month || ''}
          onSelectMonth={async (month) => {
            const monthPlan = allMonthPlans.find(mp => mp.month === month);
            if (monthPlan) {
              onBudgetMonthChange(index, monthPlan.id);
            }
          }}
          onClose={() => setShowMonthPicker(false)}
        />
      )}

      {/* Card Header - Collapsible */}
      <div 
        className={`px-4 py-3 flex items-center justify-between cursor-pointer ${getHeaderStyle()}`}
        onClick={() => onToggleExpand(index)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-bold text-[#1a1a1a]">
              {t('transaction.transaction_number', { number: index + 1 })}
            </span>
            {hasError && (
              <span className="text-xs text-red-600 font-mono">⚠️</span>
            )}
          </div>
          {!isExpanded && (
            <p className="text-xs text-gray-600 font-mono truncate mt-1">
              {getSummaryText() || t('transaction.multi.incomplete')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {mode === 'add' && !isOnlyCard && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(index);
              }}
              className="p-1 bg-red-500 border-2 border-red-700 hover:bg-red-600"
              style={{ boxShadow: '2px 2px 0 #991b1b', transition: 'none' }}
              title={t('transaction.multi.remove')}
            >
              <X className="w-4 h-4 text-white" />
            </button>
          )}
          {isExpanded ? <ChevronUp className="w-5 h-5 text-[#077f5d]" /> : <ChevronDown className="w-5 h-5 text-[#077f5d]" />}
        </div>
      </div>

      {/* Error Message */}
      {hasError && errorMessage && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200">
          <p className="text-xs text-red-600 font-mono">{errorMessage}</p>
        </div>
      )}

      {/* Card Content - Expandable */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Transaction Type */}
          <div>
            <label className="block text-[#077f5d] mb-2 uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', fontWeight: 700 }}>
              {t('transaction.type_label')}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {(['expense', 'income', 'transfer'] as const).map((typeOption) => (
                <button
                  key={typeOption}
                  type="button"
                  onClick={() => handleTypeChange(typeOption)}
                  className={`py-2 px-2 font-bold border-2 leading-tight text-xs ${
                    type === typeOption
                      ? typeOption === 'expense' ? 'bg-red-500 text-white border-red-700'
                        : typeOption === 'income' ? 'bg-[#3cbe8c] text-white border-[#077f5d]'
                        : 'bg-blue-500 text-white border-blue-700'
                      : 'bg-white text-[#077f5d] border-[#077f5d] hover:bg-[#dffbd0]'
                  }`}
                  style={{
                    boxShadow: type === typeOption
                      ? typeOption === 'expense' ? '2px 2px 0 #991b1b'
                        : typeOption === 'income' ? '2px 2px 0 #077f5d'
                        : '2px 2px 0 #1e40af'
                      : '2px 2px 0 #077f5d',
                    fontFamily: "'IBM Plex Mono', monospace"
                  }}
                >
                  {t(`transaction.${typeOption}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Account Selection */}
          {type === 'transfer' ? (
            <>
              {/* From Account */}
              <div>
                <label className="block text-[#077f5d] mb-2 uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', fontWeight: 700 }}>
                  {t('transaction.from_account')}
                </label>
                <select
                  value={fromAccount}
                  onChange={(e) => onUpdate(index, { fromAccount: e.target.value, hasError: false })}
                  className="w-full px-3 py-2 bg-white border-2 border-[#077f5d] outline-none font-mono text-sm"
                  style={{ boxShadow: '2px 2px 0 #077f5d' }}
                >
                  <option value="">{t('transaction.select_source_account')}</option>
                  {accounts.filter(a => a.status === 'active').map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} - {formatMoney(account.balance)}
                    </option>
                  ))}
                </select>
              </div>
              {/* To Account */}
              <div>
                <label className="block text-[#077f5d] mb-2 uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', fontWeight: 700 }}>
                  {t('transaction.to_account')}
                </label>
                <select
                  value={toAccount}
                  onChange={(e) => onUpdate(index, { toAccount: e.target.value, hasError: false })}
                  className="w-full px-3 py-2 bg-white border-2 border-[#077f5d] outline-none font-mono text-sm"
                  style={{ boxShadow: '2px 2px 0 #077f5d' }}
                >
                  <option value="">{t('transaction.select_destination_account')}</option>
                  {accounts.filter(a => a.status === 'active' && a.id !== fromAccount).map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} - {formatMoney(account.balance)}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <div>
              <label className="block text-[#077f5d] mb-2 uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', fontWeight: 700 }}>
                {type === 'income' ? t('transaction.destination_fund') : t('transaction.source_fund')}
              </label>
              <select
                value={selectedAccount}
                onChange={(e) => onUpdate(index, { selectedAccount: e.target.value, hasError: false })}
                className="w-full px-3 py-2 bg-white border-2 border-[#077f5d] outline-none font-mono text-sm"
                style={{ boxShadow: '2px 2px 0 #077f5d' }}
              >
                <option value="">{t('transaction.select_fund_source')}</option>
                {accounts.filter(a => a.status === 'active').map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} - {formatMoney(account.balance)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Budget Month Selector */}
          {(type === 'expense' || type === 'income' || (type === 'transfer' && categorySplits.length > 0)) && (
            <div>
              <label className="block text-[#077f5d] mb-2 uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', fontWeight: 700 }}>
                {type === 'income' ? t('transaction.set_for_month') : t('transaction.use_budget_month')}
              </label>
              <button
                type="button"
                onClick={() => setShowMonthPicker(true)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-white border-2 border-[#077f5d] hover:bg-[#dffbd0]"
                style={{ boxShadow: '2px 2px 0 #077f5d' }}
              >
                <Calendar className="w-4 h-4 text-[#077f5d]" />
                <span className="flex-1 text-left text-[#1a1a1a] font-mono text-sm">
                  {selectedBudgetMonthPlan && allMonthPlans.find(mp => mp.id === selectedBudgetMonthPlan)
                    ? formatMonth(allMonthPlans.find(mp => mp.id === selectedBudgetMonthPlan)!.month)
                    : t('transaction.select_month')}
                </span>
                <ChevronDown className="w-4 h-4 text-[#077f5d]" />
              </button>
            </div>
          )}

          {/* Category Selector - for expense and transfer (optional for transfer) */}
          {(type === 'expense' || type === 'transfer') && (
            <CategorySelector
              categories={budgetMonthCategories}
              selectedCategoryIds={categorySplits.map(s => s.category_id)}
              onToggleCategory={handleToggleCategory}
              onBatchToggle={handleBatchToggleCategories}
              loading={loadingBudgetCategories}
              type={type}
            />
          )}

          {/* Selected categories with amounts - for expense and transfer */}
          {(type === 'expense' || type === 'transfer') && categorySplits.length > 0 && (
            <div className="bg-[#f5f5dc] border-2 border-[#077f5d] p-4 space-y-3" style={{ boxShadow: '2px 2px 0 #077f5d' }}>
              {categorySplits.map((split) => (
                <div key={split.category_id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1a1a1a] font-mono truncate text-sm">
                        {split.parentName} - {split.categoryName}
                      </p>
                      <p className="text-xs text-[#077f5d] font-mono">
                        {split.displayLabel || t('transaction.remaining')}: {formatMoney(split.remainingAllocation || 0)}
                      </p>
                    </div>
                    {mode === 'add' && (
                      <button
                        type="button"
                        onClick={() => handleRemoveSplit(split.category_id)}
                        className="p-1 bg-red-500 border-2 border-red-700 hover:bg-red-600"
                        style={{ boxShadow: '2px 2px 0 #991b1b' }}
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    )}
                  </div>
                  <MoneyInput
                    value={split.amount || 0}
                    onValueChange={(value) => handleUpdateSplitAmount(split.category_id, value)}
                    placeholder="0"
                    className="w-full text-[#1a1a1a] outline-none bg-white border-2 border-[#077f5d] px-2 py-1 font-mono text-base"
                    style={{ boxShadow: '2px 2px 0 #077f5d' }}
                    allowNegative={false}
                    useCustomKeyboard={true}
                    keyboardTitle={split.categoryName}
                  />
                  <button
                    type="button"
                    onClick={() => handlePayFullSplit(split.category_id)}
                    disabled={!split.remainingAllocation || split.remainingAllocation <= 0}
                    className="w-full py-1.5 bg-[#3cbe8c] border-2 border-[#077f5d] text-white text-xs font-bold hover:bg-[#2da877] disabled:opacity-50 font-mono"
                    style={{ boxShadow: '2px 2px 0 #077f5d' }}
                  >
                    {t('transaction.pay_full')}
                  </button>
                </div>
              ))}
              
              {/* Total row */}
              <div className="border-t-2 border-[#077f5d] pt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#077f5d] uppercase font-mono">{t('transaction.amount')}</span>
                  {categorySplits.length > 1 && (
                    <button
                      type="button"
                      onClick={handlePayAllFull}
                      className="px-2 py-1 bg-[#3cbe8c] border-2 border-[#077f5d] text-white text-xs hover:bg-[#2da877] font-mono"
                      style={{ boxShadow: '2px 2px 0 #077f5d' }}
                    >
                      {t('transaction.pay_all')}
                    </button>
                  )}
                </div>
                <div className="text-[#1a1a1a] font-mono text-xl font-bold">
                  {formatMoney(totalFromSplits)}
                </div>
              </div>
            </div>
          )}

          {/* Amount field - for income, transfer without categories */}
          {(type === 'income' || (type === 'transfer' && categorySplits.length === 0) || (type === 'expense' && categorySplits.length === 0)) && (
            <div>
              <label className="block text-[#077f5d] mb-2 uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', fontWeight: 700 }}>
                {t('transaction.amount')}
              </label>
              <MoneyInput
                value={amount}
                onValueChange={(value) => onUpdate(index, { amount: value })}
                placeholder="0"
                className="w-full text-[#1a1a1a] outline-none font-mono bg-white border-2 border-[#077f5d] px-3 py-2 text-xl"
                style={{ boxShadow: '2px 2px 0 #077f5d' }}
                allowNegative={false}
                useCustomKeyboard={true}
                keyboardTitle={t('transaction.amount')}
              />
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-[#077f5d] mb-2 uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', fontWeight: 700 }}>
              {t('transaction.description')}
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => onUpdate(index, { description: e.target.value })}
              placeholder={t('transaction.description_placeholder')}
              className="w-full px-3 py-2 bg-white border-2 border-[#077f5d] outline-none font-mono text-sm"
              style={{ boxShadow: '2px 2px 0 #077f5d' }}
            />
          </div>

          {/* Date */}
          <div>
            <label className="block text-[#077f5d] mb-2 uppercase" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', fontWeight: 700 }}>
              {t('transaction.date')}
            </label>
            <input
              type="date"
              value={date || new Date().toISOString().split('T')[0]}
              onChange={(e) => onUpdate(index, { date: e.target.value || new Date().toISOString().split('T')[0] })}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 bg-white border-2 border-[#077f5d] outline-none font-mono text-sm"
              style={{ boxShadow: '2px 2px 0 #077f5d' }}
              required
            />
          </div>
        </div>
      )}
    </div>
  );
}
